import { useMemo } from 'react';
import { MembershipTier } from '../types.ts';
import {
  TIER_CONFIG,
  TIER_LEVEL,
  TierFeature,
  TierLimit,
  tierHasFeature,
  tierGetLimit,
  isUnlimited,
  minimumTierForFeature,
  tierNameKey,
} from '../tierConfig.ts';
import { useSessionStore } from '../stores/useSessionStore.ts';

export interface TierAccess {
  tier: MembershipTier;
  /** De gebruiker (en dus zijn abonnement) is binnen: zonder is `tier` een 'free' uit gebrek aan gegevens. */
  tierKnown: boolean;
  storedTier: MembershipTier;
  limits: (typeof TIER_CONFIG)['free'];
  hasFeature: (feature: TierFeature) => boolean;
  getLimit: (limit: TierLimit) => number;
  isUnlimited: (limit: TierLimit) => boolean;
  requiresUpgrade: (feature: TierFeature) => boolean;
  minimumTierFor: (feature: TierFeature) => MembershipTier;
  isFree: boolean;
  isPro: boolean;
  isBusiness: boolean;
  /** #1730 — business via een organisatielicentie: heet Bureau. */
  isBureau: boolean;
  /** i18n-sleutel van de naam die de gebruiker ziet (tierName_*). */
  tierNameKey: string;
  isTrialActive: boolean;
  trialDaysLeft: number;
  trialEndsAt: Date | null;
  /** Er loopt een Stripe-abonnement (actief, in proef bij Stripe, of betaling achterstallig). */
  hasSubscription: boolean;
}

// Zelfde lijst als shared/tierResolver.js (server).
const LOPEND_ABONNEMENT = ['active', 'trialing', 'past_due'];

export function useTierAccess(): TierAccess {
  // #839 — lees user uit reactieve Zustand store i.p.v. localStorage direct.
  // De vorige implementatie las JSON.parse(localStorage.getItem('af_user'))
  // per render, maar React heeft geen subscription op localStorage — dus
  // tier-badge bleef stale tot een full session-refresh, ook al was sessionStore
  // al geüpdatet (bv. door /api/auth/me na org-creatie of Stripe-success).
  const storeUser = useSessionStore((s) => s.user);
  const user: Record<string, unknown> = useMemo(() => {
    if (storeUser) return storeUser as unknown as Record<string, unknown>;
    try {
      return JSON.parse(localStorage.getItem('af_user') || '{}');
    } catch {
      return {};
    }
  }, [storeUser]);

  const storedTier: MembershipTier = (user.tier as MembershipTier) || 'free';
  const effectiveTier: MembershipTier = (user.effectiveTier as MembershipTier) || storedTier;

  const tierKnown = !!(user.effectiveTier || user.tier);

  const org = user.organization as { license?: { assigned?: boolean } | null } | null | undefined;
  const isBureau = effectiveTier === 'business' && !!org?.license?.assigned;

  // #1761 — op de ruwe waarde memoïseren: een nieuw Date-object per render maakte
  // het hele resultaat (en hasFeature) elke render nieuw; PlaceManager bouwde
  // daardoor telkens een nieuwe kaart, zonder markers, voor elk proefaccount.
  const trialEndsAtRaw = (user.trialEndsAt as string | undefined) || null;
  const trialEndsAt = useMemo(
    () => (trialEndsAtRaw ? new Date(trialEndsAtRaw) : null),
    [trialEndsAtRaw]
  );
  const now = new Date();
  // #1854 (audit B19) — wie een abonnement heeft, is niet meer 'in de proef', ook niet als
  // de proefdatum nog in de toekomst ligt (kopen tijdens de proef, of afschrijven pas daarna).
  const hasSubscription = LOPEND_ABONNEMENT.includes(String(user.subscription_status || ''));
  const isTrialActive =
    trialEndsAt !== null && trialEndsAt > now && storedTier !== 'free' && !hasSubscription;
  const trialDaysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
    : 0;

  return useMemo(
    () => ({
      tier: effectiveTier,
      tierKnown,
      storedTier,
      limits: TIER_CONFIG[effectiveTier],
      hasFeature: (feature: TierFeature) => tierHasFeature(effectiveTier, feature),
      getLimit: (limit: TierLimit) => tierGetLimit(effectiveTier, limit),
      isUnlimited: (limit: TierLimit) => isUnlimited(effectiveTier, limit),
      requiresUpgrade: (feature: TierFeature) => !tierHasFeature(effectiveTier, feature),
      minimumTierFor: (feature: TierFeature) => minimumTierForFeature(feature),
      isFree: effectiveTier === 'free',
      isPro: effectiveTier === 'pro',
      isBusiness: effectiveTier === 'business',
      isBureau,
      tierNameKey: tierNameKey(effectiveTier, isBureau),
      isTrialActive,
      trialDaysLeft,
      trialEndsAt,
      hasSubscription,
    }),
    [
      effectiveTier,
      tierKnown,
      storedTier,
      isBureau,
      isTrialActive,
      trialDaysLeft,
      trialEndsAt,
      hasSubscription,
    ]
  );
}
